/**
 * heatmapEngine.ts — Analytical computation layer for OperationalHeatmap
 *
 * Pure functions, no side effects, no React, no Supabase.
 * Designed for reuse in simulation, export, and comparison modes.
 */

import type { HeatmapCell, CapacityConfig, DayIndex } from './heatmapService';

// ─── Enriched cell ────────────────────────────────────────────────────────────

export type UtilizationStatus = 'empty' | 'ok' | 'attention' | 'bottleneck';

export interface EnrichedCell extends HeatmapCell {
  utilization: number;         // 0–∞  (load / capacity)
  status: UtilizationStatus;
  estimatedMinutes: number;    // estimated total load minutes
  isForecast: boolean;
}

// ─── Bottleneck window ────────────────────────────────────────────────────────

export interface BottleneckWindow {
  day: DayIndex;
  startHour: number;
  endHour: number;
  peakUtilization: number;
}

// ─── Forecast cell ────────────────────────────────────────────────────────────

export interface ForecastCell {
  day: DayIndex;
  hour: number;
  utilization: number;
  count: number;
}

// ─── Trend ───────────────────────────────────────────────────────────────────

export type Trend = 'up' | 'down' | 'stable';

// ─── Color scale (utilization-based) ─────────────────────────────────────────

/** Returns background color for a cell based on utilization (0–∞).
 *  isDark = true → midnight/black palette; false → light palette. */
export function getUtilizationColor(utilization: number, isDark = true): string {
  if (utilization === 0) return 'var(--bg-elevated)';
  if (isDark) {
    if (utilization < 0.5) return '#1E3A5F';
    if (utilization < 0.8) return '#2F6FED';
    if (utilization < 1.0) return '#F59E0B';
    return '#EF4444';
  }
  // Light mode — visible, saturated, AA-contrast ready
  if (utilization < 0.5) return '#D1FAE5'; // green-100
  if (utilization < 0.8) return '#93C5FD'; // blue-300
  if (utilization < 1.0) return '#FCD34D'; // amber-300
  return '#F87171'; // red-400
}

/** Returns foreground (text) color for a cell so text is always readable.
 *  Uses WCAG AA thresholds per background lightness. */
export function getHeatmapTextColor(utilization: number, isDark = true): string {
  if (isDark) {
    // Dark mode: low util uses dim white; medium+ uses bright white
    return utilization >= 0.5 ? '#E6EDF3' : 'rgba(200,210,220,0.9)';
  }
  // Light mode backgrounds are light below 0.8, dark above
  if (utilization < 0.8) return '#111827'; // dark text on green/blue
  if (utilization < 1.0) return '#78350F'; // dark amber text on amber-300
  return '#FFFFFF';                          // white on red
}

/** Gap-mode overlay color by utilization. */
export function getGapOverlayColor(utilization: number, isDark = true): string {
  if (isDark) {
    if (utilization < 0.8) return '#16A34A33';
    if (utilization < 1.0) return '#F59E0B55';
    return '#EF444488';
  }
  // Light mode: stronger opacity so tints are visible on white surface
  if (utilization < 0.8) return '#BBF7D0'; // green-200
  if (utilization < 1.0) return '#FDE68A'; // amber-200
  return '#FECACA';                          // red-200
}

function utilizationStatus(u: number): UtilizationStatus {
  if (u === 0) return 'empty';
  if (u < 0.8) return 'ok';
  if (u < 1.0) return 'attention';
  return 'bottleneck';
}

// ─── Cell enrichment ──────────────────────────────────────────────────────────

/**
 * Enrich a raw HeatmapCell with utilization and status.
 * When capacityConfig is provided, utilization = count / totalCapacityPerHour.
 * Otherwise, falls back to cell.intensity (already normalized 0–1).
 */
export function enrichCell(
  cell: HeatmapCell,
  capacityConfig: CapacityConfig | undefined,
  simMultiplier = 1,
): EnrichedCell {
  const count = Math.round(cell.count * simMultiplier);
  const estimatedMinutes =
    cell.avgQueueMinutes != null
      ? cell.avgQueueMinutes * count
      : count * 30;

  const utilization = capacityConfig
    ? count / Math.max(0.001, capacityConfig.totalCapacityPerHour)
    : cell.intensity ?? (count > 0 ? Math.min(count / 4, 2) : 0);

  return {
    ...cell,
    count,
    estimatedMinutes,
    utilization,
    status: utilizationStatus(utilization),
    isForecast: false,
  };
}

/** Enrich all cells in a matrix. */
export function enrichCells(
  cells: HeatmapCell[],
  capacityConfig: CapacityConfig | undefined,
  simMultiplier = 1,
): Map<string, EnrichedCell> {
  const map = new Map<string, EnrichedCell>();
  for (const cell of cells) {
    const key = `${cell.day}-${cell.hour}`;
    map.set(key, enrichCell(cell, capacityConfig, simMultiplier));
  }
  return map;
}

// ─── Bottleneck detection ─────────────────────────────────────────────────────

/**
 * Detect contiguous overloaded windows for a given day.
 * Returns sorted list of windows where utilization ≥ 1.0.
 */
export function detectBottlenecks(
  enrichedMap: Map<string, EnrichedCell>,
  hours: number[],
  day: DayIndex,
): BottleneckWindow[] {
  const windows: BottleneckWindow[] = [];
  let current: BottleneckWindow | null = null;

  for (const h of hours) {
    const cell = enrichedMap.get(`${day}-${h}`);
    const isBottleneck = (cell?.utilization ?? 0) >= 1.0;

    if (isBottleneck && cell) {
      if (!current) {
        current = { day, startHour: h, endHour: h + 1, peakUtilization: cell.utilization };
      } else {
        current.endHour = h + 1;
        current.peakUtilization = Math.max(current.peakUtilization, cell.utilization);
      }
    } else {
      if (current) { windows.push(current); current = null; }
    }
  }
  if (current) windows.push(current);
  return windows;
}

// ─── Simple forecast ──────────────────────────────────────────────────────────

const FORECAST_LOOKBACK = 3;
const FORECAST_AHEAD    = 3;

/**
 * Project the next FORECAST_AHEAD hours for a given day using a rolling average
 * of the last FORECAST_LOOKBACK real data points.
 * Returns forecast cells for hours not yet in the enriched map.
 */
export function computeForecast(
  enrichedMap: Map<string, EnrichedCell>,
  hours: number[],
  day: DayIndex,
): ForecastCell[] {
  const realHours = hours.filter(h => (enrichedMap.get(`${day}-${h}`)?.count ?? 0) > 0);
  if (realHours.length < 2) return [];

  const lastN = realHours.slice(-FORECAST_LOOKBACK);
  const avgUtil =
    lastN.reduce((s, h) => s + (enrichedMap.get(`${day}-${h}`)?.utilization ?? 0), 0) /
    lastN.length;
  const avgCount =
    Math.round(lastN.reduce((s, h) => s + (enrichedMap.get(`${day}-${h}`)?.count ?? 0), 0) /
    lastN.length);

  const lastRealHour = realHours[realHours.length - 1];
  const forecastHours = hours.filter(
    h => h > lastRealHour && !enrichedMap.has(`${day}-${h}`),
  ).slice(0, FORECAST_AHEAD);

  // Decay factor: each step ahead, confidence reduces
  return forecastHours.map((h, i) => ({
    day,
    hour: h,
    utilization: avgUtil * Math.pow(0.85, i),
    count: Math.round(avgCount * Math.pow(0.85, i)),
  }));
}

// ─── Trend ───────────────────────────────────────────────────────────────────

/**
 * Compute trend for a given day based on the last 3 non-zero data points.
 */
export function computeTrend(
  enrichedMap: Map<string, EnrichedCell>,
  hours: number[],
  day: DayIndex,
): Trend {
  const utilValues = hours
    .map(h => enrichedMap.get(`${day}-${h}`)?.utilization ?? 0)
    .filter(u => u > 0);

  if (utilValues.length < 3) return 'stable';

  const last3 = utilValues.slice(-3);
  const delta = last3[2] - last3[0];
  if (delta > 0.15) return 'up';
  if (delta < -0.15) return 'down';
  return 'stable';
}

// ─── Load line data ───────────────────────────────────────────────────────────

/** Returns total load (sum of utilization) per hour for a given day, for SVG overlay. */
export function computeLoadLine(
  enrichedMap: Map<string, EnrichedCell>,
  hours: number[],
  day: DayIndex,
): { hour: number; utilization: number }[] {
  return hours.map(h => ({
    hour: h,
    utilization: enrichedMap.get(`${day}-${h}`)?.utilization ?? 0,
  }));
}
